import React, { useEffect, useRef, useState } from 'react';
import './opEventTooltip.css';

const FADE_DURATION_MS = 200;

/**
 * Delayed hover tooltip rendered for a scheduler event. Positioned next to
 * the mouse cursor, and fades in/out rather than popping in and out.
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the tooltip should be visible
 * @param {Object} props.event - FullCalendar event object being hovered
 * @param {{ x: number, y: number }} props.mousePos - Cursor position when the tooltip was triggered
 * @param {(event: Object) => import('react').ReactNode} [props.eventContent] - Same render hook used for the event body
 */
function OpEventTooltip({ show, event, mousePos, eventContent }) {
  const [rendered, setRendered] = useState(false);
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef(null);
  const contentRef = useRef({ event, mousePos });

  if (show && event && mousePos) {
    contentRef.current = { event, mousePos };
  }

  useEffect(() => {
    if (show) {
      clearTimeout(hideTimerRef.current);
      setRendered(true);
      // Mount at opacity 0 first, then transition to opacity 1 on the next frame.
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }

    setVisible(false);
    hideTimerRef.current = setTimeout(() => setRendered(false), FADE_DURATION_MS);
    return () => clearTimeout(hideTimerRef.current);
  }, [show]);

  if (!rendered) return null;

  const { event: renderedEvent, mousePos: renderedMousePos } = contentRef.current;
  if (!renderedEvent || !renderedMousePos) return null;

  const style = {
    top: renderedMousePos.y + 12,
    left: renderedMousePos.x + 12,
  };

  return (
    <div
      className={`op-scheduler-event-tooltip ${visible ? 'op-scheduler-event-tooltip--visible' : ''}`.trim()}
      style={style}
    >
      {typeof eventContent === 'function'
        ? eventContent({ event: renderedEvent })
        : renderedEvent.title}
    </div>
  );
}

export default OpEventTooltip;
